'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationCenter from './NotificationCenter';
import RealtimeNotification from './RealtimeNotification';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Use notifications hook only when user is authenticated
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    realtimeNotification,
    markAsRead,
    markAllAsRead,
    dismissRealtimeNotification
  } = useNotifications();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSearchQuery('');
    }
  }, [isSearchOpen]);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    if (isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log('Searching for:', searchQuery);
      setIsSearchOpen(false);
    }
  };

  const handleHeaderSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (headerSearchQuery.trim()) {
      router.push(`/products?search=${encodeURIComponent(headerSearchQuery.trim())}`);
      setHeaderSearchQuery('');
    }
  };

  return (
    <>
      <header className="bg-black text-white sticky top-0 z-50">
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <div className="relative flex items-center">
              <Link
                href="/homepage"
                className="flex items-center group transition-opacity duration-fast hover:opacity-70"
              >
                <AppImage
                  src="/assets/images/tmpp7j0b0a3-1763915680565.jpg"
                  alt="CR Elevator logo - Return to Homepage"
                  width={200}
                  height={60}
                  className="h-12 lg:h-14 w-auto transition-transform duration-fast group-hover:scale-105"
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center">
              <Link href="/homepage" className="hover:text-red-600 transition-colors font-medium">
                Home
              </Link>
              <Link href="/products" className="hover:text-red-600 transition-colors font-medium">
                Products
              </Link>
              <Link href="/resources" className="hover:text-red-600 transition-colors font-medium">
                Resources
              </Link>
              <Link href="/cab-configurator" className="hover:text-red-600 transition-colors font-medium">
                Cab Configurator
              </Link>
              <Link href="/project-specification-form" className="hover:text-red-600 transition-colors font-medium">
                Project Specs
              </Link>
              <Link href="/contact" className="hover:text-red-600 transition-colors font-medium">
                Contact
              </Link>
            </nav>

            {/* Right Side Actions */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Persistent SKU/Part Number Search Bar */}
              <form onSubmit={handleHeaderSearch} className="flex items-center">
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={headerSearchQuery}
                    onChange={(e) => setHeaderSearchQuery(e.target.value)}
                    placeholder="Search by Part # or Brand..."
                    className="bg-gray-900 border border-gray-700 text-white text-sm px-4 py-2 pr-10 w-52 xl:w-64 focus:outline-none focus:border-red-600 transition-colors placeholder-gray-500"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Search parts"
                  >
                    <Icon name="MagnifyingGlassIcon" size={16} />
                  </button>
                </div>
              </form>

              {/* Notification Bell - Only show for authenticated users */}
              {user && (
                <div className="relative" ref={notificationsRef}>
                  <button
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className="p-2 text-white transition-opacity duration-fast hover:opacity-70 relative"
                    aria-label="Notifications"
                  >
                    <Icon name="BellIcon" size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full min-w-[18px]">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {isNotificationsOpen && (
                    <NotificationCenter
                      notifications={notifications}
                      unreadCount={unreadCount}
                      loading={notificationsLoading}
                      onMarkAsRead={markAsRead}
                      onMarkAllAsRead={markAllAsRead}
                      onClose={() => setIsNotificationsOpen(false)}
                    />
                  )}
                </div>
              )}

              {/* Login / My Account Button */}
              {user ? (
                <Link
                  href="/customer-portal"
                  className="px-5 py-3 bg-transparent text-white text-sm font-semibold tracking-wide border border-white transition-all duration-fast hover:bg-white hover:text-black whitespace-nowrap flex items-center gap-2"
                >
                  <Icon name="UserCircleIcon" size={18} />
                  My Account
                </Link>
              ) : (
                <Link
                  href="/customer-login"
                  className="px-5 py-3 bg-transparent text-white text-sm font-semibold tracking-wide border border-white transition-all duration-fast hover:bg-white hover:text-black whitespace-nowrap flex items-center gap-2"
                >
                  <Icon name="UserCircleIcon" size={18} />
                  Login
                </Link>
              )}

              {/* CTA Button */}
              <Link
                href="/contact"
                className="px-6 py-3 bg-red-600 text-white text-sm font-semibold tracking-wide border border-red-600 transition-all duration-fast hover:bg-red-700 hover:border-red-700 whitespace-nowrap"
              >
                Request A Quote
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white transition-opacity duration-fast hover:opacity-70"
              aria-label="Toggle menu"
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
              />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden bg-black border-t border-gray-800">
            <div className="container mx-auto px-6 py-4">
              {/* Mobile Search */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const input = (e.target as HTMLFormElement).querySelector('input') as HTMLInputElement;
                  if (input?.value.trim()) {
                    router.push(`/products?search=${encodeURIComponent(input.value.trim())}`);
                    setIsMobileMenuOpen(false);
                  }
                }}
                className="mb-4"
              >
                <div className="relative flex items-center">
                  <input
                    type="text"
                    placeholder="Search by Part # or Brand..."
                    className="w-full bg-gray-900 border border-gray-700 text-white text-sm px-4 py-3 pr-10 focus:outline-none focus:border-red-600 transition-colors placeholder-gray-500"
                  />
                  <button
                    type="submit"
                    className="absolute right-3 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Search"
                  >
                    <Icon name="MagnifyingGlassIcon" size={18} />
                  </button>
                </div>
              </form>

              <div className="flex flex-col space-y-4">
                <Link
                  href="/homepage"
                  className="hover:text-red-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  href="/products"
                  className="hover:text-red-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Products
                </Link>
                <Link
                  href="/resources"
                  className="hover:text-red-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Resources
                </Link>
                <Link
                  href="/cab-configurator"
                  className="hover:text-red-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Cab Configurator
                </Link>
                <Link
                  href="/project-specification-form"
                  className="hover:text-red-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Project Specs
                </Link>
                <Link
                  href="/contact"
                  className="hover:text-red-600 transition-colors font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contact
                </Link>

                {/* Login / My Account - Mobile */}
                {user ? (
                  <Link
                    href="/customer-portal"
                    className="flex items-center gap-2 hover:text-red-600 transition-colors font-medium py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon name="UserCircleIcon" size={18} />
                    My Account
                  </Link>
                ) : (
                  <Link
                    href="/customer-login"
                    className="flex items-center gap-2 hover:text-red-600 transition-colors font-medium py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon name="UserCircleIcon" size={18} />
                    Login
                  </Link>
                )}
                
                {/* Mobile CTA Button */}
                <Link
                  href="/contact"
                  className="mt-4 px-6 py-3 bg-red-600 text-white text-sm font-semibold tracking-wide border border-red-600 transition-all duration-fast hover:bg-red-700 hover:border-red-700 text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Request A Quote
                </Link>
              </div>
            </div>
          </nav>
        )}
      </header>

      {/* Real-time Notification Toast */}
      <RealtimeNotification
        notification={realtimeNotification}
        onDismiss={dismissRealtimeNotification}
      />
    </>
  );
}